import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Table, Spinner, Button, ProgressBar } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const C = {
  navy:   '#0f172a', navy2:  '#1e293b', sky:    '#0ea5e9', skyD:   '#0369a1',
  teal:   '#0d9488', tealD:  '#0f766e', green:  '#10b981', greenD: '#059669',
  amber:  '#f59e0b', violet: '#6366f1', red:    '#ef4444', redD:   '#dc2626',
  slate:  '#f8fafc', border: '#e2e8f0', text:   '#1e293b', muted:  '#64748b', light:  '#94a3b8',
};

const fmt = (v) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(v || 0);

const fmtNum = (v) =>
  new Intl.NumberFormat('id-ID').format(v || 0);

const Pill = ({ bg, color, children, outline }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', padding: '1px 6px',
    borderRadius: 12, fontSize: '0.66rem', lineHeight: 1.2, fontWeight: 600,
    background: outline ? 'transparent' : (bg || '#f1f5f9'),
    color: color || C.muted,
    border: outline ? `1px solid ${bg || C.border}` : 'none',
    whiteSpace: 'nowrap'
  }}>
    {children}
  </span>
);

const ActionBtn = ({ icon, title, color, onClick, disabled }) => (
  <button 
    title={title} 
    onClick={onClick}
    disabled={disabled}
    style={{
      background: 'transparent',
      border: `1px solid ${color}`,
      borderRadius: 5,
      color,
      width: 24,
      height: 24,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '0.72rem',
      padding: 0,
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.15s'
    }}
    onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = color; e.currentTarget.style.color = '#fff'; } }}
    onMouseLeave={e => { if (!disabled) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = color; } }}
  >
    <i className={`bi ${icon}`} style={{ pointerEvents: 'none' }} />
  </button>
);

const PaginationBar = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemLabel = 'data',
  itemsPerPageOptions = [10, 25, 50, 100]
}) => {
  const [jumpVal, setJumpVal] = useState('');

  const startIdx = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endIdx = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  const pages = getPageNumbers();

  const handleJump = (e) => {
    e?.preventDefault?.();
    const p = parseInt(jumpVal, 10);
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      onPageChange(p);
      setJumpVal('');
    }
  };

  return (
    <div style={{
      padding: '8px 14px',
      borderTop: `1px solid ${C.border}`,
      background: '#fafbfc',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 12
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.76rem', color: C.muted }}>
          Menampilkan <strong style={{ color: C.navy }}>{startIdx.toLocaleString('id-ID')}</strong> - <strong style={{ color: C.navy }}>{endIdx.toLocaleString('id-ID')}</strong> dari <strong style={{ color: C.navy }}>{totalItems.toLocaleString('id-ID')}</strong> {itemLabel}
        </div>
        {onItemsPerPageChange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: C.muted }}>
            <span>Tampilkan</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              style={{
                padding: '2px 6px',
                borderRadius: 5,
                border: `1px solid ${C.border}`,
                background: '#fff',
                color: C.navy,
                fontSize: '0.74rem',
                fontWeight: 600,
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {itemsPerPageOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <span>baris</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          style={{
            padding: '3px 7px',
            borderRadius: 5,
            border: `1px solid ${C.border}`,
            background: currentPage === 1 ? '#f1f5f9' : '#fff',
            color: currentPage === 1 ? '#94a3b8' : C.navy,
            fontSize: '0.74rem',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
          }}
          title="Halaman Pertama"
        >
          <i className="bi bi-chevron-double-left" />
        </button>

        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: '3px 7px',
            borderRadius: 5,
            border: `1px solid ${C.border}`,
            background: currentPage === 1 ? '#f1f5f9' : '#fff',
            color: currentPage === 1 ? '#94a3b8' : C.navy,
            fontSize: '0.74rem',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
          }}
          title="Halaman Sebelumnya"
        >
          <i className="bi bi-chevron-left" />
        </button>

        <div style={{ display: 'flex', gap: 3 }}>
          {pages.map((p, idx) => (
            p === '...' ? (
              <span key={`dots-${idx}`} style={{ padding: '3px 5px', fontSize: '0.74rem', color: C.muted }}>...</span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                style={{
                  minWidth: 26,
                  height: 26,
                  padding: '0 5px',
                  borderRadius: 5,
                  border: `1px solid ${p === currentPage ? C.teal : C.border}`,
                  background: p === currentPage ? C.teal : '#fff',
                  color: p === currentPage ? '#fff' : C.navy,
                  fontSize: '0.74rem',
                  fontWeight: p === currentPage ? 700 : 500,
                  cursor: 'pointer'
                }}
              >
                {p}
              </button>
            )
          ))}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            padding: '3px 7px',
            borderRadius: 5,
            border: `1px solid ${C.border}`,
            background: (currentPage === totalPages || totalPages === 0) ? '#f1f5f9' : '#fff',
            color: (currentPage === totalPages || totalPages === 0) ? '#94a3b8' : C.navy,
            fontSize: '0.74rem',
            cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer'
          }}
          title="Halaman Selanjutnya"
        >
          <i className="bi bi-chevron-right" />
        </button>

        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            padding: '3px 7px',
            borderRadius: 5,
            border: `1px solid ${C.border}`,
            background: (currentPage === totalPages || totalPages === 0) ? '#f1f5f9' : '#fff',
            color: (currentPage === totalPages || totalPages === 0) ? '#94a3b8' : C.navy,
            fontSize: '0.74rem',
            cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer'
          }}
          title="Halaman Terakhir"
        >
          <i className="bi bi-chevron-double-right" />
        </button>

        <form onSubmit={handleJump} style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 6 }}>
          <input
            type="number"
            min="1"
            max={totalPages || 1}
            value={jumpVal}
            onChange={(e) => setJumpVal(e.target.value)}
            placeholder="Hal"
            style={{
              width: 44,
              padding: '2px 4px',
              fontSize: '0.74rem',
              textAlign: 'center',
              outline: 'none'
            }}
          />
          <button
            type="submit"
            style={{
              padding: '2px 7px',
              borderRadius: 5,
              border: `1px solid ${C.border}`,
              background: '#f8fafc',
              fontSize: '0.72rem',
              color: C.navy,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Go
          </button>
        </form>
      </div>
    </div>
  );
};

const SuratPesananApotek = () => {
  const navigate = useNavigate();

  // Header state
  const [username, setUsername] = useState('Administrator');
  const [time, setTime] = useState({ date: '', clock: '' });

  // Main data state
  const [lokasi, setLokasi] = useState('00001'); // 00001 = Apotek, 00002 = Farmasi
  const [pesananList, setPesananList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [summaryData, setSummaryData] = useState({ total_items: 0, total_butuh: 0, total_pesan: 0, grand_total: 0 });

  // Toast notification
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ show: false, message: '', variant: 'success' }), 4000);
  };

  // Top Yellow Input Row State (for direct medicine entry)
  const [inputRow, setInputRow] = useState({
    kd_obat: '',
    nm_obat: '',
    satuanjual: '',
    satuanbeli: '',
    kdsp: '',
    nmsp: '',
    penjualan: 0,
    stok: 0,
    butuh: 0,
    minorder: 1,
    hrgjadi: 0,
    pesan: 0,
    prinsipal: ''
  });

  // Autocomplete drug lookup
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [isSearchingLookup, setIsSearchingLookup] = useState(false);
  const [showLookupDropdown, setShowLookupDropdown] = useState(false);
  const lookupRef = useRef(null);

  // Modal State
  const [isCalculating, setIsCalculating] = useState(false);
  const [isProcessingSp, setIsProcessingSp] = useState(false);
  const [showModalConfirmHitug, setShowModalConfirmHitug] = useState(false);
  const [showModalConfirmProses, setShowModalConfirmProses] = useState(false);
  const [showModalBatalSp, setShowModalBatalSp] = useState(false);
  const [barangBatalList, setBarangBatalList] = useState([]);

  // Modal Cetak Ulang (History SP)
  const [showModalCetakUlang, setShowModalCetakUlang] = useState(false);
  const [spHistoryList, setSpHistoryList] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  // Modal PDF Viewer In-App
  const [showModalPdf, setShowModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfTitle, setPdfTitle] = useState('Surat Pesanan (SP)');
  const [pdfFilename, setPdfFilename] = useState('Surat_Pesanan.pdf');

  // Clock tick
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setTime({
        date: n.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        clock: n.toLocaleTimeString('id-ID') + ' WIB'
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Set user
  useEffect(() => {
    const savedUser = localStorage.getItem('nama_user') || localStorage.getItem('username') || localStorage.getItem('user');
    if (savedUser) setUsername(savedUser);
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Click outside lookup dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (lookupRef.current && !lookupRef.current.contains(e.target)) {
        setShowLookupDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Pesanan TMPSP
  const fetchPesanan = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = `${getApiUrl()}/show_pesananapotek?lokasi=${lokasi}&search=${encodeURIComponent(debouncedSearch)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Server status ${res.status}`);
      const j = await res.json();
      if (j && j.status === 1) {
        setPesananList(j.data || []);
        setSummaryData({
          total_items: j.total || (j.data || []).length,
          total_butuh: j.total_butuh || 0,
          total_pesan: j.total_pesan || 0,
          grand_total: j.grand_total || 0
        });
      } else {
        setPesananList([]);
      }
    } catch (err) {
      console.error('Gagal fetch pesanan:', err);
      showToast('Gagal memuat data pesanan: ' + err.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  }, [lokasi, debouncedSearch]);

  useEffect(() => {
    fetchPesanan();
  }, [fetchPesanan]);

  // Handle Autocomplete Lookup on input row
  const handleSearchDrug = async (text) => {
    setLookupQuery(text);
    if (!text || text.length < 2) {
      setLookupResults([]);
      setShowLookupDropdown(false);
      return;
    }
    setIsSearchingLookup(true);
    setShowLookupDropdown(true);
    try {
      const res = await fetch(`${getApiUrl()}/cari_obat_sp?lokasi=${lokasi}&q=${encodeURIComponent(text)}`);
      if (res.ok) {
        const j = await res.json();
        setLookupResults(j.data || []);
      }
    } catch (e) {
      console.error('Error lookup drug:', e);
    } finally {
      setIsSearchingLookup(false);
    }
  };

  // Select drug from lookup to fill input row
  const handleSelectDrug = (d) => {
    setInputRow({
      kd_obat: d.kd_obat,
      nm_obat: d.nm_obat,
      satuanjual: d.satuanjual,
      satuanbeli: d.satuanbeli,
      kdsp: d.kdsp || '',
      nmsp: d.nmsp || '-',
      penjualan: d.penjualan || 0,
      stok: d.stok || 0,
      minorder: d.minorder || 1,
      hrgjadi: d.hrgjadi || 0,
      pesan: d.minorder || 1,
      prinsipal: d.prinsipal || '-'
    });
    setLookupQuery(`${d.kd_obat} ${d.nm_obat}`);
    setShowLookupDropdown(false);
  };

  // Add item from input row to TMPSP
  const handleAddItem = async () => {
    if (!inputRow.kd_obat || !inputRow.nm_obat) {
      showToast('Pilih obat terlebih dahulu!', 'warning');
      return;
    }
    if (inputRow.pesan <= 0) {
      showToast('Jumlah pesan harus lebih dari 0!', 'warning');
      return;
    }

    try {
      const payload = {
        lokasi,
        kd_obat: inputRow.kd_obat,
        nm_obat: inputRow.nm_obat,
        satuanjual: inputRow.satuanjual,
        kdsp: inputRow.kdsp,
        nmsp: inputRow.nmsp,
        penjualan: inputRow.penjualan,
        stok: inputRow.stok,
        minorder: inputRow.minorder,
        hrgjadi: inputRow.hrgjadi,
        qty: inputRow.pesan,
        pesan: inputRow.pesan,
        prinsipal: inputRow.prinsipal
      };

      const res = await fetch(`${getApiUrl()}/simpan_item_pesanan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const j = await res.json();
      if (res.ok && j.status === 1) {
        showToast(j.msg || 'Item berhasil ditambahkan!', 'success');
        setInputRow({
          kd_obat: '', nm_obat: '', satuanjual: '', satuanbeli: '',
          kdsp: '', nmsp: '', penjualan: 0, stok: 0, butuh: 0,
          minorder: 1, hrgjadi: 0, pesan: 0, prinsipal: ''
        });
        setLookupQuery('');
        fetchPesanan();
      } else {
        showToast(j.msg || 'Gagal menyimpan item', 'danger');
      }
    } catch (e) {
      showToast('Terjadi kesalahan: ' + e.message, 'danger');
    }
  };

  // Update inline Pesan quantity in table
  const handleUpdateQty = async (item, newQty) => {
    const q = parseInt(newQty, 10);
    if (isNaN(q) || q < 0) return;

    try {
      const res = await fetch(`${getApiUrl()}/update_item_pesanan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lokasi,
          kd_obat: item.kd_obat,
          qty: q,
          pesan: q
        })
      });
      if (res.ok) {
        setPesananList(prev => prev.map(row => {
          if (row.kd_obat === item.kd_obat) {
            return {
              ...row,
              pesan: q,
              qty: q,
              total: q * (row.hrgjadi || 0)
            };
          }
          return row;
        }));
      }
    } catch (e) {
      console.error('Gagal update qty:', e);
    }
  };

  // Delete item from TMPSP
  const handleDeleteItem = async (item) => {
    try {
      const res = await fetch(`${getApiUrl()}/hapus_item_pesanan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lokasi,
          kd_obat: item.kd_obat
        })
      });
      const j = await res.json();
      if (res.ok && j.status === 1) {
        showToast(`Obat ${item.nm_obat} dihapus dari daftar`, 'info');
        fetchPesanan();
      }
    } catch (e) {
      showToast('Gagal menghapus item: ' + e.message, 'danger');
    }
  };

  // Hitung Kebutuhan Otomatis (Red Button - Gambas cmdrekalkulasi)
  const handleHitungKebutuhan = () => {
    // Buka dialog pertanyaan konfirmasi: Ya atau Tidak
    setShowModalConfirmHitug(true);
  };

  const executeHitungKebutuhan = async () => {
    setShowModalConfirmHitug(false);
    setIsCalculating(true);
    try {
      const res = await fetch(`${getApiUrl()}/hitung_kebutuhan_apotek`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lokasi })
      });
      const j = await res.json();
      if (res.ok && j.status === 1) {
        showToast(j.msg || 'Perhitungan kebutuhan selesai!', 'success');
        fetchPesanan();
      } else {
        showToast(j.msg || 'Gagal menghitung kebutuhan', 'danger');
      }
    } catch (e) {
      showToast('Terjadi kesalahan saat hitung kebutuhan: ' + e.message, 'danger');
    } finally {
      setIsCalculating(false);
    }
  };

  // Proses Pesanan (cmdsimpan1_Click):
  // 1. Cek apakah ada barang dengan kdsp = '#NA'
  // 2. Jika tidak ada, buka konfirmasi: "Surat Pesanan akan diproses ?"
  const handleProsesSp = () => {
    const validItems = pesananList.filter(x => (x.pesan || x.qty) > 0);
    if (validItems.length === 0) {
      showToast('Tidak ada obat dengan jumlah pesan > 0 untuk diproses!', 'warning');
      return;
    }

    // Cek apakah masih ada pemasok yg #NA
    const naItems = pesananList.filter(x => (x.pesan || x.qty) > 0 && x.kdsp === '#NA' && x.flag_proses !== 'D');
    if (naItems.length > 0) {
      showToast('Masih ada pemasok yg belum dipilih, silahkan proses pemilihan pemasoknya', 'danger');
      return;
    }

    setShowModalConfirmProses(true);
  };

  // Eksekusi jika user memilih Ya pada dialog konfirmasi
  // Menjalankan cmdsimpan1_Click(): printspbatal(), cekregionalsp(), update flag = 'V'
  // Lalu baru membuka page ProsesPesananApotek
  const executeProsesSp = async () => {
    setShowModalConfirmProses(false);
    setIsProcessingSp(true);

    try {
      const res = await fetch(`${getApiUrl()}/cmd_simpan1_proses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lokasi })
      });
      const j = await res.json();

      if (!res.ok || j.status !== 1) {
        showToast(j.msg || 'Gagal memproses data Surat Pesanan', 'danger');
        setIsProcessingSp(false);
        return;
      }

      // Jika ada barang yang dibatalkan gudang (flag_proses = 'D') -> tampilkan laporan pembatalan
      if (j.barang_batal && j.barang_batal.length > 0) {
        setBarangBatalList(j.barang_batal);
        setShowModalBatalSp(true);
        setIsProcessingSp(false);
        return;
      }

      // Berhasil diverifikasi dan flag sudah diupdate -> navigasi ke form pembuatan pesanan
      showToast('Data berhasil diverifikasi. Membuka form Pembuatan Pesanan...', 'success');
      setTimeout(() => {
        setIsProcessingSp(false);
        navigate('/ProsesPesananApotek', { state: { lokasi } });
      }, 300);

    } catch (err) {
      setIsProcessingSp(false);
      showToast('Terjadi kesalahan saat memproses data: ' + err.message, 'danger');
    }
  };

  // Handler dialog pembatalan SP (printspbatal - Hapus data pembatalan SP ?)
  const handleHapusDataBatal = async (hapus) => {
    if (hapus) {
      try {
        await fetch(`${getApiUrl()}/hapus_sp_batal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lokasi })
        });
        showToast('Data pembatalan SP berhasil dibersihkan', 'info');
      } catch (e) {
        console.error(e);
      }
    }
    setShowModalBatalSp(false);
    navigate('/ProsesPesananApotek', { state: { lokasi } });
  };

  // Buka Cetak Ulang (History)
  const handleOpenCetakUlang = async () => {
    setShowModalCetakUlang(true);
    setIsLoadingHistory(true);
    setHistorySearch('');
    try {
      const res = await fetch(`${getApiUrl()}/list_sp_history?lokasi=${lokasi}`);
      if (res.ok) {
        const j = await res.json();
        setSpHistoryList(j.data || []);
      }
    } catch (e) {
      console.error('Gagal fetch history SP:', e);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Cetak ulang dari modal history
  const handlePrintHistorySp = async (noSp) => {
    setShowModalCetakUlang(false);
    setIsProcessingSp(true);
    setPdfProgress(25);
    setPdfUrl('');
    setPdfTitle(`Cetak Ulang SP: ${noSp}`);
    setPdfFilename(`SP_${noSp.replace(/\//g, '_')}.pdf`);
    setShowModalPdf(true);

    const progressTimer = setInterval(() => {
      setPdfProgress(prev => (prev < 85 ? prev + 15 : 85));
    }, 120);

    try {
      const res = await fetch(`${getApiUrl()}/cetak_sp_pdf?no_sp=${encodeURIComponent(noSp)}`);
      if (!res.ok) throw new Error('Gagal mengambil PDF Surat Pesanan');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      clearInterval(progressTimer);
      setPdfProgress(100);
      setTimeout(() => {
        setPdfUrl(url);
        setIsProcessingSp(false);
      }, 200);
    } catch (e) {
      clearInterval(progressTimer);
      setIsProcessingSp(false);
      setShowModalPdf(false);
      showToast('Gagal cetak ulang: ' + e.message, 'danger');
    }
  };

  // Pagination calculation
  const totalPages = Math.ceil(pesananList.length / itemsPerPage) || 1;
  const paginatedData = pesananList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const thStyle = {
    background: '#005577',
    color: '#ffffff',
    fontSize: '0.73rem',
    fontWeight: 700,
    letterSpacing: '0.3px',
    padding: '7px 8px',
    border: '1px solid #004466',
    verticalAlign: 'middle',
    textAlign: 'center',
    whiteSpace: 'nowrap'
  };

  const tdStyle = {
    fontSize: '0.74rem',
    padding: '5px 8px',
    border: '1px solid #e2e8f0',
    verticalAlign: 'middle'
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', flexDirection: 'column', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
      
      {/* TOAST ALERT */}
      {toast.show && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999,
          background: toast.variant === 'success' ? '#065f46' : toast.variant === 'warning' ? '#92400e' : '#991b1b',
          color: '#fff', padding: '10px 18px', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)', fontSize: '0.84rem', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 10
        }}>
          <i className={`bi ${toast.variant === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`} />
          <span>{toast.message}</span>
        </div>
      )}

      {/* TOPBAR / HEADER (Dark Navy Theme) */}
      <header style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        color: '#fff',
        padding: '8px 18px',
        borderBottom: '2px solid #0d9488',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12
      }}>
        {/* Brand & Hospital Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img
            src="/logo.png"
            alt="Logo RS"
            style={{
              width: 40,
              height: 40,
              objectFit: 'contain',
              background: '#fff',
              borderRadius: 8,
              padding: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
            }}
          />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 800, fontSize: '1.02rem', letterSpacing: '0.5px' }}>PESANAN OBAT & AKSP</span>
              <Pill bg="rgba(13,148,136,0.3)" color="#2dd4bf" outline>Surat Pesanan Apotek</Pill>
            </div>
            <div style={{ fontSize: '0.70rem', color: '#94a3b8', lineHeight: 1.2 }}>
              <span style={{ color: '#e2e8f0', fontWeight: 700 }}>RS MARY CILEUNGSI HIJAU</span> &bull; Jl. Raya Narogong KM 21 Cileungsi Bogor &bull; Telp. (021) 8249-2222 Fax. (021) 8249-3902
            </div>
          </div>
        </div>

        {/* Navigation Shortcut & User Session */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Quick Nav to Master Obat */}
          <button
            onClick={() => navigate('/MasterObat')}
            className="btn btn-sm btn-outline-light d-flex align-items-center gap-1.5"
            style={{ fontSize: '0.76rem', borderRadius: 6, padding: '4px 10px', borderColor: 'rgba(255,255,255,0.25)' }}
            title="Buka Katalog Master Obat"
          >
            <i className="bi bi-capsule" />
            <span>Katalog Master Obat</span>
          </button>

          {/* Quick Nav to Tanda Terima Barang */}
          <button
            onClick={() => navigate('/TerimaBarangApotek', { state: { lokasi } })}
            className="btn btn-sm btn-outline-info d-flex align-items-center gap-1.5"
            style={{ fontSize: '0.76rem', borderRadius: 6, padding: '4px 10px', borderColor: 'rgba(56,189,248,0.4)', color: '#38bdf8' }}
            title="Buka Penerimaan Barang / TTB Apotek"
          >
            <i className="bi bi-box-seam" />
            <span>Tanda Terima Barang</span>
          </button>

          {/* Lokasi Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)', padding: '3px 8px', borderRadius: 6 }}>
            <span style={{ fontSize: '0.72rem', color: '#cbd5e1' }}>Lokasi:</span>
            <select
              value={lokasi}
              onChange={(e) => setLokasi(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#38bdf8',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="00001" style={{ color: '#0f172a' }}>Apotek (00001)</option>
              <option value="00002" style={{ color: '#0f172a' }}>Farmasi (00002)</option>
            </select>
          </div>

          {/* Clock */}
          <div style={{ textAlign: 'right', fontSize: '0.72rem', color: '#94a3b8', borderLeft: '1px solid rgba(255,255,255,0.15)', paddingLeft: 12 }}>
            <div style={{ color: '#e2e8f0', fontWeight: 600 }}>{time.clock}</div>
            <div>{time.date}</div>
          </div>

          {/* User badge */}
          <div style={{
            background: 'rgba(255,255,255,0.08)',
            padding: '4px 10px',
            borderRadius: 8,
            fontSize: '0.75rem',
            color: '#e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            <i className="bi bi-person-circle text-info" />
            <span>{username}</span>
          </div>

          {/* Back to Home */}
          <button
            onClick={() => navigate('/Home')}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 6,
              color: '#fff',
              width: 30,
              height: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '0.82rem'
            }}
            title="Kembali ke Dashboard Utama"
          >
            <i className="bi bi-house-door-fill" />
          </button>
        </div>
      </header>

      {/* TOP RED BANNER: HITUNG KEBUTUHAN (Replicating Gambas Screenshot) */}
      <div style={{ padding: '8px 16px 0 16px' }}>
        <button
          onClick={handleHitungKebutuhan}
          disabled={isCalculating}
          style={{
            width: '100%',
            background: 'linear-gradient(180deg, #cc0000 0%, #aa0000 100%)',
            color: '#ffffff',
            border: '1px solid #880000',
            borderRadius: 4,
            padding: '7px 12px',
            fontSize: '0.98rem',
            fontWeight: 800,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            boxShadow: '0 2px 6px rgba(170,0,0,0.3)',
            cursor: isCalculating ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s'
          }}
          onMouseEnter={e => { if (!isCalculating) e.currentTarget.style.background = '#dd0000'; }}
          onMouseLeave={e => { if (!isCalculating) e.currentTarget.style.background = 'linear-gradient(180deg, #cc0000 0%, #aa0000 100%)'; }}
        >
          {isCalculating ? (
            <>
              <Spinner animation="border" size="sm" />
              <span>Sedang Menghitung Kebutuhan Obat...</span>
            </>
          ) : (
            <>
              <i className="bi bi-calculator-fill" style={{ fontSize: '1.1rem' }} />
              <span>HITUNG KEBUTUHAN</span>
            </>
          )}
        </button>
      </div>

      {/* SUMMARY INFO STRIP */}
      <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 10px', fontSize: '0.74rem' }}>
            Total Obat: <strong style={{ color: '#0f172a' }}>{summaryData.total_items}</strong> item
          </div>
          <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 10px', fontSize: '0.74rem' }}>
            Total Butuh: <strong style={{ color: '#d97706' }}>{fmtNum(summaryData.total_butuh)}</strong>
          </div>
          <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 10px', fontSize: '0.74rem' }}>
            Total Pesan: <strong style={{ color: '#059669' }}>{fmtNum(summaryData.total_pesan)}</strong>
          </div>
          <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, padding: '4px 12px', fontSize: '0.76rem', color: '#065f46' }}>
            Estimasi Nilai SP: <strong>{fmt(summaryData.grand_total)}</strong>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={fetchPesanan}
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            style={{ fontSize: '0.72rem', padding: '3px 8px' }}
            title="Refresh Data"
          >
            <i className="bi bi-arrow-clockwise" /> Refresh
          </button>
        </div>
      </div>

      {/* MAIN CONTENT / TABLE AREA */}
      <div style={{ flex: 1, padding: '0 16px 12px 16px', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          background: '#fff',
          border: '2px solid #005577',
          borderRadius: 4,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
        }}>
          
          {/* SCROLLABLE TABLE CONTAINER */}
          <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 275px)', minHeight: 380 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', borderSpacing: 0 }}>
              <thead>
                {/* Header Baris 1 */}
                <tr>
                  <th rowSpan={2} style={{ ...thStyle, width: 38 }}>NO</th>
                  <th rowSpan={2} style={{ ...thStyle, textAlign: 'left', minWidth: 260 }}>
                    NAMA OBAT - AKSP STANDAR YANG DIBUTUHKAN
                  </th>
                  <th rowSpan={2} style={{ ...thStyle, width: 90 }}>SATUAN JUAL</th>
                  <th rowSpan={2} style={{ ...thStyle, textAlign: 'left', minWidth: 180 }}>PEMASOK</th>
                  <th colSpan={3} style={{ ...thStyle, background: '#007799', borderBottom: '1px solid #005577' }}>
                    PENJUALAN
                  </th>
                  <th rowSpan={2} style={{ ...thStyle, width: 65 }}>MIN BELI</th>
                  <th rowSpan={2} style={{ ...thStyle, width: 100, textAlign: 'right' }}>HARGA JADI</th>
                  <th rowSpan={2} style={{ ...thStyle, width: 85, background: '#004466' }}>PESAN</th>
                  <th rowSpan={2} style={{ ...thStyle, width: 110, textAlign: 'right' }}>SUBTOTAL</th>
                  <th rowSpan={2} style={{ ...thStyle, width: 45 }}>AKSI</th>
                </tr>
                {/* Header Baris 2 (Sub-Kolom Penjualan) */}
                <tr>
                  <th style={{ ...thStyle, width: 55, background: '#e07700', borderTop: 'none' }}>7 HR.</th>
                  <th style={{ ...thStyle, width: 55, background: '#e07700', borderTop: 'none' }}>STOK</th>
                  <th style={{ ...thStyle, width: 60, background: '#e07700', borderTop: 'none' }}>BUTUH</th>
                </tr>

                {/* YELLOW INPUT ROW (Replicating Gambas Quick Input Row) */}
                <tr style={{ background: '#ffff88', borderBottom: '2px solid #eab308' }}>
                  <td style={{ ...tdStyle, textAlign: 'center', color: '#854d0e', fontWeight: 700 }}>&bull;</td>
                  
                  {/* Autocomplete Input Kode & Nama Obat */}
                  <td style={{ ...tdStyle, position: 'relative' }} ref={lookupRef}>
                    <input
                      type="text"
                      value={lookupQuery}
                      onChange={(e) => handleSearchDrug(e.target.value)}
                      placeholder="Ketik Kode atau Nama Obat untuk ditambah..."
                      style={{
                        width: '100%',
                        padding: '3px 8px',
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        border: '1px solid #ca8a04',
                        borderRadius: 3,
                        background: '#fff',
                        outline: 'none'
                      }}
                    />
                    {/* Autocomplete Dropdown */}
                    {showLookupDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 999,
                        background: '#fff',
                        border: '1px solid #0284c7',
                        borderRadius: 4,
                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                        maxHeight: 220,
                        overflowY: 'auto'
                      }}>
                        {isSearchingLookup ? (
                          <div style={{ padding: '8px 12px', fontSize: '0.74rem', color: C.muted }}>Mencari katalog obat...</div>
                        ) : lookupResults.length > 0 ? (
                          lookupResults.map((drug) => (
                            <div
                              key={drug.kd_obat}
                              onClick={() => handleSelectDrug(drug)}
                              style={{
                                padding: '6px 10px',
                                borderBottom: '1px solid #f1f5f9',
                                cursor: 'pointer',
                                fontSize: '0.74rem'
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                            >
                              <div style={{ fontWeight: 700, color: '#0f172a' }}>
                                <span style={{ color: '#0284c7', marginRight: 6 }}>[{drug.kd_obat}]</span>
                                {drug.nm_obat}
                              </div>
                              <div style={{ fontSize: '0.68rem', color: '#64748b', display: 'flex', gap: 10, marginTop: 2 }}>
                                <span>Satuan: {drug.satuanjual}</span>
                                <span>Pemasok: {drug.nmsp}</span>
                                <span>Stok: <strong>{drug.stok}</strong></span>
                                <span>HDA: {fmt(drug.hrgjadi)}</span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '8px 12px', fontSize: '0.74rem', color: C.muted }}>Tidak ada obat ditemukan.</div>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Satuan Jual Display */}
                  <td style={{ ...tdStyle, textAlign: 'center', fontSize: '0.72rem', fontWeight: 600 }}>
                    {inputRow.satuanjual || '-'}
                  </td>

                  {/* Pemasok Display */}
                  <td style={{ ...tdStyle, fontSize: '0.72rem' }}>
                    {inputRow.nmsp || '-'}
                  </td>

                  {/* 7 Hr */}
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
                    {inputRow.penjualan}
                  </td>

                  {/* Stok */}
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
                    {inputRow.stok}
                  </td>

                  {/* Butuh */}
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#b45309' }}>
                    {inputRow.butuh}
                  </td>

                  {/* Min Beli */}
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
                    {inputRow.minorder}
                  </td>

                  {/* Harga Jadi */}
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                    {fmt(inputRow.hrgjadi)}
                  </td>

                  {/* Pesan Input */}
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input
                      type="number"
                      min="0"
                      value={inputRow.pesan}
                      onChange={(e) => setInputRow(prev => ({ ...prev, pesan: parseInt(e.target.value, 10) || 0 }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddItem(); }}
                      style={{
                        width: 60,
                        padding: '2px 4px',
                        fontSize: '0.76rem',
                        fontWeight: 700,
                        textAlign: 'center',
                        border: '1px solid #ca8a04',
                        borderRadius: 3,
                        outline: 'none',
                        background: '#fff'
                      }}
                    />
                  </td>

                  {/* Subtotal Display */}
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#065f46' }}>
                    {fmt(inputRow.pesan * inputRow.hrgjadi)}
                  </td>

                  {/* Add Button */}
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={handleAddItem}
                      title="Tambahkan ke daftar pesanan"
                      style={{
                        background: '#059669',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 3,
                        padding: '2px 6px',
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        fontWeight: 700
                      }}
                    >
                      <i className="bi bi-plus-lg" />
                    </button>
                  </td>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', padding: '40px 10px', color: C.muted }}>
                      <Spinner animation="border" size="sm" className="me-2 text-primary" />
                      <span>Memuat data pesanan obat...</span>
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((item, idx) => {
                    const rowNumber = (currentPage - 1) * itemsPerPage + idx + 1;
                    const isYellowHighlight = item.kd_obat === '00021723';
                    const rowBg = isYellowHighlight ? '#ffffcc' : (idx % 2 === 1 ? '#f8fafc' : '#ffffff');
                    const itemSubtotal = (item.pesan || item.qty || 0) * (item.hrgjadi || 0);

                    return (
                      <tr 
                        key={item.kd_obat} 
                        style={{ background: rowBg, transition: 'background 0.1s' }}
                        onMouseEnter={e => { if (!isYellowHighlight) e.currentTarget.style.background = '#f0f9ff'; }}
                        onMouseLeave={e => { if (!isYellowHighlight) e.currentTarget.style.background = rowBg; }}
                      >
                        <td style={{ ...tdStyle, textAlign: 'center', color: C.muted }}>{rowNumber}</td>
                        
                        {/* Nama Obat + Kode */}
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#005577', fontSize: '0.72rem' }}>
                              {item.kd_obat}
                            </span>
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>
                              {item.nm_obat}
                            </span>
                          </div>
                        </td>

                        {/* Satuan Jual */}
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <Pill bg="#f1f5f9" color="#475569">{item.satuanjual || '-'}</Pill>
                        </td>

                        {/* Pemasok */}
                        <td style={{ ...tdStyle, color: '#334155' }}>
                          {item.nmsp || '-'}
                        </td>

                        {/* Penjualan 7 Hari */}
                        <td style={{ ...tdStyle, textAlign: 'center', color: '#475569' }}>
                          {item.penjualan || 0}
                        </td>

                        {/* Stok */}
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, color: (item.stok <= 0 ? C.redD : '#0f172a') }}>
                          {item.stok || 0}
                        </td>

                        {/* Butuh */}
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#b45309' }}>
                          {item.butuh || item.qty || 0}
                        </td>

                        {/* Min Beli */}
                        <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>
                          {item.minorder || 1}
                        </td>

                        {/* Harga Jadi */}
                        <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                          {fmtNum(item.hrgjadi)}
                        </td>

                        {/* Pesan (Interactive Numeric Input) */}
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            defaultValue={item.pesan || item.qty || 0}
                            onBlur={(e) => handleUpdateQty(item, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateQty(item, e.target.value);
                                e.target.blur();
                              }
                            }}
                            style={{
                              width: 58,
                              padding: '2px 4px',
                              fontSize: '0.76rem',
                              fontWeight: 700,
                              textAlign: 'center',
                              border: `1px solid ${C.border}`,
                              borderRadius: 4,
                              background: (item.pesan || item.qty) > 0 ? '#f0fdf4' : '#fff',
                              color: (item.pesan || item.qty) > 0 ? '#15803d' : '#94a3b8',
                              outline: 'none'
                            }}
                          />
                        </td>

                        {/* Subtotal */}
                        <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                          {fmtNum(itemSubtotal)}
                        </td>

                        {/* Action: Delete */}
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <ActionBtn
                            icon="bi-trash3"
                            title="Hapus dari daftar pesanan"
                            color={C.red}
                            onClick={() => handleDeleteItem(item)}
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', padding: '40px 10px', color: C.muted }}>
                      <div style={{ fontSize: '1.8rem', opacity: 0.4, marginBottom: 6 }}>
                        <i className="bi bi-inbox" />
                      </div>
                      <div style={{ fontWeight: 600, color: '#475569' }}>Belum ada item pesanan obat.</div>
                      <div style={{ fontSize: '0.74rem', marginTop: 4 }}>
                        Klik tombol merah <strong>"HITUNG KEBUTUHAN"</strong> di atas atau ketik nama obat pada baris kuning untuk menambah item.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION BAR */}
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={pesananList.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(limit) => { setItemsPerPage(limit); setCurrentPage(1); }}
            itemLabel="item pesanan"
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        </div>
      </div>

      {/* BOTTOM ACTION BAR (Replicating Gambas Screenshot Bottom Panel) */}
      <footer style={{
        background: '#e2e8f0',
        borderTop: '2px solid #cbd5e1',
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12
      }}>
        {/* Left: Quick Search / Filter Input Field */}
        <div style={{ flex: '1 1 300px', maxWidth: 450, position: 'relative' }}>
          <i className="bi bi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cari obat atau pemasok dalam daftar pesanan..."
            style={{
              width: '100%',
              padding: '6px 12px 6px 32px',
              fontSize: '0.78rem',
              borderRadius: 4,
              border: '1px solid #cbd5e1',
              background: '#fff',
              outline: 'none'
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.78rem'
              }}
            >
              <i className="bi bi-x-circle-fill" />
            </button>
          )}
        </div>

        {/* Right: Action Buttons (PROSES, CETAK ULANG, KELUAR) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Button PROSES (Blue gradient with spinner / gear icon) */}
          <button
            onClick={handleProsesSp}
            disabled={isProcessingSp || pesananList.length === 0}
            style={{
              background: 'linear-gradient(180deg, #0284c7 0%, #0369a1 100%)',
              color: '#ffffff',
              border: '1px solid #075985',
              borderRadius: 4,
              padding: '6px 18px',
              fontSize: '0.84rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: (isProcessingSp || pesananList.length === 0) ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 5px rgba(2,132,199,0.3)',
              opacity: pesananList.length === 0 ? 0.6 : 1
            }}
          >
            {isProcessingSp ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <i className="bi bi-arrow-repeat" style={{ fontSize: '1rem' }} />
            )}
            <span>PROSES</span>
          </button>

          {/* Button CETAK ULANG (Printer icon) */}
          <button
            onClick={handleOpenCetakUlang}
            style={{
              background: '#ffffff',
              color: '#0f172a',
              border: '1px solid #94a3b8',
              borderRadius: 4,
              padding: '6px 16px',
              fontSize: '0.84rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}
          >
            <i className="bi bi-printer-fill" style={{ fontSize: '1rem', color: '#475569' }} />
            <span>CETAK ULANG</span>
          </button>

          {/* Button KELUAR (Red cross icon) */}
          <button
            onClick={() => navigate('/home')}
            style={{
              background: '#ffffff',
              color: '#dc2626',
              border: '1px solid #dc2626',
              borderRadius: 4,
              padding: '6px 16px',
              fontSize: '0.84rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}
          >
            <i className="bi bi-x-circle-fill" style={{ fontSize: '1rem', color: '#dc2626' }} />
            <span>KELUAR</span>
          </button>
        </div>
      </footer>

      {/* MODAL CETAK ULANG (History SP) */}
      <Modal 
        show={showModalCetakUlang} 
        onHide={() => setShowModalCetakUlang(false)} 
        size="lg" 
        centered
        backdrop="static"
      >
        <Modal.Header 
          closeButton 
          style={{ background: '#0f172a', color: '#fff', borderBottom: '2px solid #0d9488' }}
          className="py-2.5 px-3.5"
        >
          <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-printer-fill text-info" />
            <span>Cetak Ulang Surat Pesanan (SP)</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3" style={{ background: '#f8fafc' }}>
          <div style={{ marginBottom: 10, display: 'flex', gap: 10 }}>
            <input
              type="text"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              placeholder="Cari Nomor SP, Tanggal, atau Nama Pemasok..."
              className="form-control form-control-sm"
              style={{ fontSize: '0.8rem' }}
            />
          </div>

          <div style={{ maxHeight: 380, overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }}>
            <Table hover size="sm" className="mb-0" style={{ fontSize: '0.75rem' }}>
              <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '6px 8px', width: 40, textAlign: 'center' }}>No</th>
                  <th style={{ padding: '6px 8px' }}>Nomor SP</th>
                  <th style={{ padding: '6px 8px', width: 90, textAlign: 'center' }}>Tanggal</th>
                  <th style={{ padding: '6px 8px' }}>Nama Pemasok / Supplier</th>
                  <th style={{ padding: '6px 8px', width: 110, textAlign: 'right' }}>Total (Rp)</th>
                  <th style={{ padding: '6px 8px', width: 90, textAlign: 'center' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingHistory ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px' }}>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Memuat riwayat SP...
                    </td>
                  </tr>
                ) : spHistoryList.filter(sp => {
                  if (!historySearch) return true;
                  const q = historySearch.toLowerCase();
                  return (sp.no_sp || '').toLowerCase().includes(q) ||
                         (sp.tanggal || '').toLowerCase().includes(q) ||
                         (sp.nm_suplier || '').toLowerCase().includes(q);
                }).length > 0 ? (
                  spHistoryList.filter(sp => {
                    if (!historySearch) return true;
                    const q = historySearch.toLowerCase();
                    return (sp.no_sp || '').toLowerCase().includes(q) ||
                           (sp.tanggal || '').toLowerCase().includes(q) ||
                           (sp.nm_suplier || '').toLowerCase().includes(q);
                  }).map((sp, idx) => (
                    <tr key={sp.no_sp || idx}>
                      <td style={{ textAlign: 'center', color: C.muted }}>{idx + 1}</td>
                      <td style={{ fontWeight: 700, color: '#0284c7' }}>{sp.no_sp}</td>
                      <td style={{ textAlign: 'center' }}>{sp.tanggal}</td>
                      <td>{sp.nm_suplier || '-'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtNum(sp.total)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => handlePrintHistorySp(sp.no_sp)}
                          className="btn btn-xs btn-primary d-flex align-items-center gap-1 mx-auto"
                          style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                        >
                          <i className="bi bi-printer" /> Cetak
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: C.muted }}>
                      Tidak ada data riwayat Surat Pesanan.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2 px-3 bg-light d-flex justify-content-between align-items-center">
          <small className="text-muted">Pilih dokumen SP untuk dicetak ulang langsung dalam format PDF.</small>
          <Button variant="secondary" size="sm" onClick={() => setShowModalCetakUlang(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL PRATINJAU PDF SURAT PESANAN (In-App Viewer) */}
      <Modal 
        show={showModalPdf} 
        onHide={() => { 
          setShowModalPdf(false); 
          if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl('');
          }
        }} 
        size="xl" 
        centered 
        backdrop="static"
      >
        <Modal.Header 
          closeButton={!isProcessingSp} 
          style={{ background: '#0f172a', color: '#fff', borderBottom: '2px solid #0d9488' }} 
          className="py-2 px-3 d-flex justify-content-between align-items-center"
        >
          <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 600 }}>
            <i className="bi bi-file-earmark-pdf-fill text-danger me-2" />
            {pdfTitle}
          </Modal.Title>
          {pdfUrl && (
            <div className="d-flex align-items-center gap-2 me-3">
              <a 
                href={pdfUrl} 
                download={pdfFilename} 
                className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                style={{ fontSize: '0.78rem', padding: '3px 8px' }}
              >
                <i className="bi bi-download" /> Unduh PDF
              </a>
            </div>
          )}
        </Modal.Header>
        <Modal.Body className="p-0 bg-light position-relative" style={{ height: '80vh' }}>
          {pdfUrl ? (
            <iframe 
              src={pdfUrl} 
              title={pdfTitle} 
              width="100%" 
              height="100%" 
              style={{ border: 'none' }}
            />
          ) : (
            <div className="d-flex flex-column h-100 justify-content-center align-items-center bg-white p-4">
              <div className="text-center mb-4">
                <i className="bi bi-file-earmark-pdf text-teal" style={{ fontSize: '4.5rem', color: '#0d9488', opacity: 0.9 }} />
                <h5 className="mt-3 fw-bold text-dark">Membuat Dokumen Surat Pesanan PDF...</h5>
                <p className="text-muted small">Sedang memproses data pesanan dan merender dokumen SP dari server.</p>
              </div>
              <div style={{ width: '60%', maxWidth: '500px' }}>
                <ProgressBar 
                  animated 
                  now={pdfProgress} 
                  label={`${pdfProgress}%`} 
                  variant="primary" 
                  style={{ height: '24px', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 'bold' }} 
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2 px-3 bg-light d-flex justify-content-between align-items-center">
          <small className="text-muted">
            <i className="bi bi-info-circle me-1" />
            Gunakan kontrol PDF pada viewer untuk memperbesar, memutar, atau mencetak dokumen ke printer.
          </small>
          <Button 
            variant="secondary" 
            size="sm" 
            disabled={isProcessingSp}
            onClick={() => { 
              setShowModalPdf(false); 
              if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
                setPdfUrl('');
              }
            }}
          >
            Tutup Pratinjau
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DIALOG KONFIRMASI HITUNG KEBUTUHAN (YA / TIDAK) */}
      <Modal 
        show={showModalConfirmHitug} 
        onHide={() => setShowModalConfirmHitug(false)} 
        centered
        backdrop="static"
      >
        <Modal.Header 
          style={{ 
            background: 'linear-gradient(to right, #991b1b, #b91c1c)', 
            color: '#fff', 
            padding: '10px 16px',
            borderBottom: 'none'
          }}
        >
          <Modal.Title style={{ fontSize: '0.96rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-question-circle-fill" style={{ fontSize: '1.2rem', color: '#fef08a' }} />
            Konfirmasi Hitung Kebutuhan
          </Modal.Title>
          <button 
            type="button" 
            onClick={() => setShowModalConfirmHitug(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: '1.1rem',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 24px', background: '#fff' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <i className="bi bi-calculator-fill" style={{ fontSize: '1.4rem', color: '#dc2626' }} />
            </div>
            <div>
              <h6 style={{ fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                Apakah kebutuhan barang / obat akan dihitung?
              </h6>
              <p style={{ fontSize: '0.84rem', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                Seluruh barang <strong>STANDAR</strong> yang <strong>DIJUAL</strong> untuk lokasi <strong>{lokasi === '00001' ? 'Apotek (00001)' : 'Farmasi (00002)'}</strong> akan dihitung otomatis berdasarkan stok persediaan, rata-rata penjualan riwayat, dan standar sedia.
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '10px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => setShowModalConfirmHitug(false)}
            style={{ minWidth: 80, fontWeight: 600, fontSize: '0.82rem' }}
          >
            <i className="bi bi-x-lg me-1" /> Tidak
          </Button>
          <Button 
            variant="danger" 
            size="sm" 
            onClick={executeHitungKebutuhan}
            style={{ minWidth: 80, fontWeight: 600, fontSize: '0.82rem', background: '#dc2626', borderColor: '#b91c1c' }}
          >
            <i className="bi bi-check-lg me-1" /> Ya, Hitung
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DIALOG KONFIRMASI PROSES PESANAN (YA / TIDAK) */}
      <Modal 
        show={showModalConfirmProses} 
        onHide={() => setShowModalConfirmProses(false)} 
        centered
        backdrop="static"
      >
        <Modal.Header 
          style={{ 
            background: 'linear-gradient(to right, #0369a1, #0284c7)', 
            color: '#fff', 
            padding: '10px 16px',
            borderBottom: 'none'
          }}
        >
          <Modal.Title style={{ fontSize: '0.96rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-question-circle-fill" style={{ fontSize: '1.2rem', color: '#7dd3fc' }} />
            Konfirmasi Proses Pesanan
          </Modal.Title>
          <button 
            type="button" 
            onClick={() => setShowModalConfirmProses(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: '1.1rem',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 24px', background: '#fff' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: '#e0f2fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <i className="bi bi-arrow-repeat" style={{ fontSize: '1.4rem', color: '#0284c7' }} />
            </div>
            <div>
              <h6 style={{ fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                Surat Pesanan akan diproses ?
              </h6>
              <p style={{ fontSize: '0.84rem', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                Sistem akan memverifikasi pemasok, mengecek penyesuaian regional SP, dan membuka form Pembuatan Pesanan untuk <strong>{pesananList.length} item obat</strong> di lokasi <strong>{lokasi === '00001' ? 'Apotek (00001)' : 'Farmasi (00002)'}</strong>.
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '10px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => setShowModalConfirmProses(false)}
            style={{ minWidth: 80, fontWeight: 600, fontSize: '0.82rem' }}
          >
            <i className="bi bi-x-lg me-1" /> Tidak
          </Button>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={executeProsesSp}
            style={{ minWidth: 80, fontWeight: 600, fontSize: '0.82rem', background: '#0284c7', borderColor: '#0369a1' }}
          >
            <i className="bi bi-check-lg me-1" /> Ya
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DIALOG DAFTAR PESANAN YANG DIBATALKAN GUDANG (printspbatal) */}
      <Modal
        show={showModalBatalSp}
        onHide={() => handleHapusDataBatal(false)}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header style={{ background: '#991b1b', color: '#fff', padding: '10px 16px' }}>
          <Modal.Title style={{ fontSize: '0.94rem', fontWeight: 800 }}>
            <i className="bi bi-exclamation-triangle-fill me-2 text-warning" />
            DAFTAR PESANAN YANG DIBATALKAN GUDANG
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 14 }}>
          <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 10 }}>
            Terdapat <strong>{barangBatalList.length} item</strong> pesanan yang dibatalkan oleh gudang:
          </p>
          <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
            <Table hover size="sm" style={{ fontSize: '0.74rem', margin: 0 }}>
              <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                <tr>
                  <th>Nama Obat</th>
                  <th>Prinsipal</th>
                  <th style={{ textAlign: 'right' }}>Harga</th>
                  <th>Satuan</th>
                  <th style={{ textAlign: 'right' }}>Isi</th>
                  <th style={{ textAlign: 'right' }}>Nett</th>
                  <th style={{ textAlign: 'right' }}>H.D.</th>
                  <th style={{ textAlign: 'right' }}>Stok</th>
                </tr>
              </thead>
              <tbody>
                {barangBatalList.map((b, idx) => {
                  const konv = parseFloat(b.konversi) || 1;
                  const pl = parseFloat(b.pricelist) || 0;
                  const nett = konv > 0 ? pl / konv : pl;
                  const hda = nett * 1.1;
                  return (
                    <tr key={idx}>
                      <td style={{ fontWeight: 600 }}>{b.nm_obat}</td>
                      <td>{b.prinsipal || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(pl)}</td>
                      <td>{b.satuanbeli || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(konv)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(nett)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(hda)}</td>
                      <td style={{ textAlign: 'right' }}>0</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div style={{ marginTop: 14, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
            <div style={{ fontWeight: 700, fontSize: '0.84rem', color: '#991b1b' }}>
              Hapus data pembatalan SP ?
            </div>
            <div style={{ fontSize: '0.76rem', color: '#7f1d1d', marginTop: 2 }}>
              Pilih "Hapus" untuk membersihkan item yang dibatalkan dari TMPSP dan lanjut ke form pemesanan, atau "Batal" untuk membiarkan item tersebut.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 14px', background: '#f8fafc' }}>
          <Button variant="outline-secondary" size="sm" onClick={() => handleHapusDataBatal(false)}>
            Batal
          </Button>
          <Button variant="danger" size="sm" onClick={() => handleHapusDataBatal(true)} style={{ fontWeight: 700 }}>
            <i className="bi bi-trash me-1" /> Hapus
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default SuratPesananApotek;
